/*
 * The places for hidden tabs that keep their connection (`socket.ts`, `HIDDEN_CLOSE_MS`).
 *
 * A tab hidden for five minutes closes its socket, except a tab whose page asks to keep it — an
 * owner's AI chat, whose container counts a tab open without focus as passive use for hours, and a
 * document, whose faces stay in its band for a meeting's length (W4-D8). The server sees either open
 * only while the tab's connection says so (`focusReporter.ts`). The tabs of one browser sign-in share
 * the server's five connections (protocol.md §2.1, §2.12), and every hidden keeper is a place taken
 * from the tabs in use, so at most `HIDDEN_KEEPERS` hidden tabs keep theirs: the rest close at five
 * minutes as every hidden tab used to.
 *
 * The places are Web Locks, one name each, so the browser gives a place back by itself when its tab
 * dies, and a tab takes one only if it is free at once (`ifAvailable`): a hidden tab never queues for
 * a place, it closes instead. Without a lock manager — an old engine, a test that passes none —
 * nothing is kept.
 *
 * **The AI chats come first.** A document takes only the last place, and holds a marker lock beside
 * it while it does, so at most one hidden document per browser keeps its socket and the first place
 * stays an AI chat's. A hidden AI chat that finds every place taken looks for the marker
 * (`navigator.locks.query()`) and, when a document holds the last place, takes it from the document
 * (`steal`): an AI chat that lost its place would close at five minutes and lose the passive use that
 * keeps its container, while a document that loses its place only loses its face two minutes later.
 * The document hears its place was taken and closes its socket.
 */

/** How many hidden tabs of one browser may keep their connection; the other places stay for tabs in use. */
export const HIDDEN_KEEPERS = 2

/** The lock names of the places, derived from the count. */
export const KEEPER_LOCKS: readonly string[] = Array.from(
	{ length: HIDDEN_KEEPERS },
	(_, i) => `teambase-hidden-keep-${i}`,
)

/** The marker a document holds beside the last place, while it holds that place. */
export const DOC_KEEPER_MARKER = "teambase-hidden-keep-doc"

/** The place a document may take: the last. */
const DOC_PLACE = KEEPER_LOCKS[KEEPER_LOCKS.length - 1]!

/**
 * The slice of `navigator.locks` a place needs; a test passes a fake shared by several sockets. A
 * lock taken from its holder (`steal`) rejects the holder's request.
 */
export interface KeepLocks {
	request(
		name: string,
		options: { ifAvailable: true } | { steal: true },
		callback: (lock: unknown) => Promise<void>,
	): Promise<unknown>
	/** Which locks are held now; an engine without it never takes a place from a document. */
	query?(): Promise<{ held?: readonly { name?: string }[] }>
}

/** Who asks for a place: an AI chat may take any, a document only the last, marked. */
export type KeeperKind = "page" | "document"

/** One tab's claim on a place: taken while its hidden page asks to keep, given back when shown. */
export class KeeperPlace {
	private readonly locks: KeepLocks | null
	/** Told when the place was taken from this tab; the socket closes then. */
	private readonly onLost: () => void
	/** Gives the place (and a document's marker) back; present exactly while the tab holds one. */
	private release: (() => void) | undefined
	/** Moves with every give-back, so a grant that lands after one is handed straight back. */
	private generation = 0

	constructor(locks: KeepLocks | null, onLost: () => void = () => {}) {
		this.locks = locks
		this.onLost = onLost
	}

	/**
	 * Takes a free place for `kind`, or keeps the one held; false when every place it may take is
	 * taken or there are no locks. An AI chat that finds every place taken takes the last from a
	 * document that holds it.
	 */
	async take(kind: KeeperKind = "page"): Promise<boolean> {
		if (this.release !== undefined) return true
		if (this.locks === null) return false
		const generation = this.generation
		if (kind === "document") return this.takeForDocument(generation)
		for (const name of KEEPER_LOCKS) {
			if (await this.hold(name, { ifAvailable: true }, generation)) return true
			if (generation !== this.generation) return false
		}
		if (!(await this.documentHoldsLastPlace()) || generation !== this.generation) return false
		return this.hold(DOC_PLACE, { steal: true }, generation)
	}

	/** Gives the place back, if one is held; a take still on its way gets none. */
	giveBack(): void {
		this.generation++
		const release = this.release
		this.release = undefined
		release?.()
	}

	/** The last place, then the marker beside it; a marker that is not free gives the place back. */
	private async takeForDocument(generation: number): Promise<boolean> {
		if (!(await this.hold(DOC_PLACE, { ifAvailable: true }, generation))) return false
		const place = this.handOver()
		const marked = await this.hold(DOC_KEEPER_MARKER, { ifAvailable: true }, generation)
		const marker = this.handOver()
		if (!marked || generation !== this.generation) {
			place?.()
			marker?.()
			return false
		}
		this.release = () => {
			marker?.()
			place?.()
		}
		return true
	}

	/** The release of the lock just held, taken out of the tab's hands so a second lock may be held. */
	private handOver(): (() => void) | undefined {
		const release = this.release
		this.release = undefined
		return release
	}

	/** Whether a hidden document holds the last place now: its marker is held. */
	private async documentHoldsLastPlace(): Promise<boolean> {
		const locks = this.locks
		if (locks?.query === undefined) return false
		try {
			const { held = [] } = await locks.query()
			const names = new Set(held.map((lock) => lock.name))
			return names.has(DOC_KEEPER_MARKER) && names.has(DOC_PLACE)
		} catch {
			return false
		}
	}

	/**
	 * Requests one lock and, when granted, keeps it until `release` runs. The holder's request is
	 * rejected when another tab steals the lock: the place is gone, so whatever else this tab held
	 * with it is given back and the socket is told.
	 */
	private hold(
		name: string,
		options: { ifAvailable: true } | { steal: true },
		generation: number,
	): Promise<boolean> {
		const locks = this.locks
		if (locks === null) return Promise.resolve(false)
		return new Promise<boolean>((answer) => {
			let granted = false
			locks
				.request(name, options, async (lock) => {
					if (lock === null || generation !== this.generation || this.release !== undefined) {
						answer(false)
						return
					}
					const held = new Promise<void>((resolve) => {
						this.release = resolve
					})
					granted = true
					answer(true)
					await held
				})
				.catch(() => {
					answer(false)
					if (!granted || generation !== this.generation) return
					this.giveBack()
					this.onLost()
				})
		})
	}
}

/** The browser's lock manager, or null where there is none. */
export function browserKeepLocks(): KeepLocks | null {
	const locks = (navigator as Partial<Navigator>).locks as LockManager | null | undefined
	return typeof locks?.request === "function" ? locks : null
}
